import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useApolloClient, gql } from '@apollo/client';
import { OrganizationMember, useGetMembersQuery } from 'gql/graphql';
import { transformDataForTable } from '../../components/table/membersTableHelpers';
import { IMembersTableProps } from '../../components/table/MembersTable';
import { useAuthenticatedAxios } from 'features/common/apiUtils/AxiosProvider';
import {
  IGetMembersDataItem,
  IGetMembersResponse,
  IMemberGroup,
  IMemberRole,
} from 'features/members/services/identity/membersModel';
import { normalizeMemberId } from 'features/members/utils/memberId';
import { identity as identityConfig } from '@sitecore-ui/portal-singular';
import { env } from 'features/common/config/envConfig';
import { AxiosInstance } from 'axios';
import { useDebounce } from 'features/common/hooks/useDebounce';

export const GET_REMOVED_MEMBER_ID = gql`
  query GetRemovedMemberId {
    removedMemberId @client
  }
`;

const identity = identityConfig(env);
const EMPTY_ORGANIZATION_MEMBERS: OrganizationMember[] = [];

type TeamsSortValue =
  | { type: 'dash' }
  | { type: 'string'; value: string }
  | { type: 'number'; value: number };

const normalizeGroups = (groups: any): IMemberGroup[] | null => {
  if (!groups) return null;
  if (Array.isArray(groups)) return groups as IMemberGroup[];
  if (Array.isArray(groups.nodes)) {
    return groups.nodes
      .map((g: any) => ({ id: g?.id, name: g?.name }))
      .filter((g: { id?: string; name?: string }) => g.id && g.name);
  }
  return null;
};

const getTeamsSortValue = (member: OrganizationMember): TeamsSortValue => {
  const groups = normalizeGroups((member as any).groups);

  if (!groups?.length) {
    return { type: 'dash' };
  }
  if (groups.length === 1) {
    return { type: 'string', value: groups[0]?.name ?? '' };
  }
  return { type: 'number', value: groups.length };
};

const getTeamsTypeRank = (
  type: TeamsSortValue['type'],
  isDescending: boolean,
): number => {
  if (isDescending) {
    return type === 'number' ? 0 : type === 'string' ? 1 : 2;
  }
  return type === 'dash' ? 0 : type === 'string' ? 1 : 2;
};

/**
 * Sort the full dataset for the Groups column, then paginate.
 * Sorting rules:
 * - Descending: numbers 9→1 → strings Z→A → "-" last
 * - Ascending:  "-" first → strings A→Z → numbers 1→9
 */
export const sortMembersForTeamsColumn = (
  members: OrganizationMember[],
  isDescending: boolean,
  page: number,
  pageSize: number,
): OrganizationMember[] => {
  const sorted = [...members].sort((a, b) => {
    const av = getTeamsSortValue(a);
    const bv = getTeamsSortValue(b);

    const typeRankDiff =
      getTeamsTypeRank(av.type, isDescending) -
      getTeamsTypeRank(bv.type, isDescending);
    if (typeRankDiff !== 0) return typeRankDiff;

    // Same type: compare within category
    if (av.type === 'dash') return 0;

    if (av.type === 'number' && bv.type === 'number') {
      return isDescending ? bv.value - av.value : av.value - bv.value;
    }

    if (av.type === 'string' && bv.type === 'string') {
      const cmp = av.value.localeCompare(bv.value, undefined, {
        sensitivity: 'base',
        numeric: true,
      });
      return isDescending ? -cmp : cmp;
    }

    return 0;
  });

  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  return sorted.slice(startIndex, endIndex);
};

type OrgRoleSortValue = { type: 'none' } | { type: 'string'; value: string };

const getOrgRoleSortValue = (member: OrganizationMember): OrgRoleSortValue => {
  const roleName = member.organizationRoles?.nodes?.[0]?.name ?? '';
  const value = String(roleName).trim();
  if (!value) return { type: 'none' };
  return { type: 'string', value };
};

/**
 * Sort the full dataset for the Individual access (orgRole) column, then paginate.
 * - Ascending: strings A→Z → empty roles last
 * - Descending: empty roles first → strings Z→A
 */
export const sortMembersForOrgRoleColumn = (
  members: OrganizationMember[],
  isDescending: boolean,
  page: number,
  pageSize: number,
): OrganizationMember[] => {
  const sorted = [...members].sort((a, b) => {
    const av = getOrgRoleSortValue(a);
    const bv = getOrgRoleSortValue(b);

    const typeRank = (type: OrgRoleSortValue['type']) =>
      isDescending ? (type === 'none' ? 0 : 1) : type === 'string' ? 0 : 1;

    const typeRankDiff = typeRank(av.type) - typeRank(bv.type);
    if (typeRankDiff !== 0) return typeRankDiff;

    // Same type rank; ensure both are strings before comparing (helps TS narrow correctly)
    if (av.type !== 'string' || bv.type !== 'string') return 0;

    const cmp = av.value.localeCompare(bv.value, undefined, {
      sensitivity: 'base',
      numeric: true,
    });
    return isDescending ? -cmp : cmp;
  });

  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  return sorted.slice(startIndex, endIndex);
};

// Map table column IDs to API sort field names
const sortFieldMap: Record<string, string> = {
  givenName: 'givenName',
  familyName: 'familyName',
  email: 'email',
  lastLogin: 'lastLogin',
};

const normalizeMemberGroupsMap = (
  map?: Map<string, IMemberGroup[]> | null,
): Map<string, IMemberGroup[]> => {
  const nextMap = new Map<string, IMemberGroup[]>();
  if (!map) return nextMap;

  map.forEach((value, key) => {
    const normalizedKey = normalizeMemberId(key) || key;
    nextMap.set(normalizedKey, value);
    if (normalizedKey !== key) {
      nextMap.set(key, value);
    }
  });

  return nextMap;
};

const GROUPS_CACHE_KEY = 'portal_member_groups_map';
const GROUPS_CACHE_EXPIRY_KEY = 'portal_member_groups_cache_expiry';
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

const GROUPS_API = {
  groupsUrl: '/organization/groups',
  groupMembersUrl: (groupId: string, pageNumber: number) =>
    `/organization/groups/${groupId}/members?pagenumber=${pageNumber}`,
};

// Load groups map from localStorage cache
export const loadGroupsFromCache = (
  organizationId?: string | null,
): Map<string, IMemberGroup[]> | null => {
  try {
    // Avoid using an unscoped cache if we don't know the org.
    if (!organizationId) return null;

    const cached = localStorage.getItem(GROUPS_CACHE_KEY);
    const expiry = localStorage.getItem(GROUPS_CACHE_EXPIRY_KEY);

    if (!cached || !expiry) return null;

    const expiryTime = parseInt(expiry, 10);
    if (Date.now() > expiryTime) {
      // Cache expired, clear it
      localStorage.removeItem(GROUPS_CACHE_KEY);
      localStorage.removeItem(GROUPS_CACHE_EXPIRY_KEY);
      return null;
    }

    // Parse cached data
    const parsed: unknown = JSON.parse(cached);
    const payload = parsed as {
      orgId?: string;
      data?: Record<string, IMemberGroup[]>;
    };

    // Validate cache belongs to the current organization
    if (!payload?.orgId || payload.orgId !== organizationId || !payload.data) {
      localStorage.removeItem(GROUPS_CACHE_KEY);
      localStorage.removeItem(GROUPS_CACHE_EXPIRY_KEY);
      return null;
    }

    const data = payload.data;
    const map = new Map<string, IMemberGroup[]>();
    Object.entries(data).forEach(([key, value]) => {
      map.set(key, value as IMemberGroup[]);
    });
    return map;
  } catch (error) {
    console.warn('Failed to load member groups cache', error);
    return null;
  }
};

// Save groups map to localStorage cache
export const saveGroupsToCache = (
  map: Map<string, IMemberGroup[]>,
  organizationId?: string | null,
): void => {
  try {
    // Avoid writing an unscoped cache if we don't know the org.
    if (!organizationId) return;

    const data: Record<string, IMemberGroup[]> = {};
    map.forEach((value, key) => {
      data[key] = value;
    });
    localStorage.setItem(
      GROUPS_CACHE_KEY,
      JSON.stringify({ orgId: organizationId, data }),
    );
    localStorage.setItem(
      GROUPS_CACHE_EXPIRY_KEY,
      String(Date.now() + CACHE_DURATION_MS),
    );
  } catch (error) {
    console.warn('Failed to save member groups cache', error);
  }
};

// Fetch groups for all members by fetching all groups and their members
// Uses REST API: GET /organization/groups and GET /organization/groups/{groupId}/members
export const fetchMemberGroups = async (
  axiosInstance: AxiosInstance,
  organizationId?: string | null,
  signal?: AbortSignal,
  bypassCache = false,
): Promise<Map<string, IMemberGroup[]>> => {
  // Try to load from cache first (unless bypassCache is true)
  if (!bypassCache) {
    const cached = loadGroupsFromCache(organizationId);
    if (cached) {
      return cached;
    }
  }

  const memberGroupsMap = new Map<string, IMemberGroup[]>();
  const pageSize = 100;
  const maxPages = 1000; // safety net if API pagination metadata is wrong
  const concurrency = 5; // limit parallel group-member calls

  try {
    // Fetch all groups using REST API
    const groupsResponse = await axiosInstance.get<{
      data: Array<{ id: string; name: string }>;
    }>(GROUPS_API.groupsUrl, { signal });
    const groups = groupsResponse.data.data || [];

    const fetchGroupMembers = async (group: { id: string; name: string }) => {
      if (signal?.aborted) return;

      try {
        let pageNumber = 1;
        let hasMore = true;
        let pagesFetched = 0;

        while (hasMore && !signal?.aborted) {
          pagesFetched++;
          if (pagesFetched > maxPages) break;

          // REST API call: GET /organization/groups/{groupId}/members?pagenumber={page}&pagesize=100
          const baseMembersUrl = GROUPS_API.groupMembersUrl(
            group.id,
            pageNumber,
          );
          const membersUrl = `${baseMembersUrl}&pagesize=${pageSize}`;
          const membersResponse = await axiosInstance.get<{
            data: Array<{ id: string }>;
            totalRecords?: number;
            pageLength?: number;
            pageNumber?: number;
          }>(membersUrl, { signal });

          const members = membersResponse.data.data || [];
          members.forEach((member: { id: string }) => {
            const existingGroups = memberGroupsMap.get(member.id) || [];
            if (!existingGroups.find((g: IMemberGroup) => g.id === group.id)) {
              memberGroupsMap.set(member.id, [
                ...existingGroups,
                { id: group.id, name: group.name },
              ]);
            }
          });

          // Pagination: prefer totalRecords when present, otherwise fall back to "did we receive a full page?"
          const totalRecords = membersResponse.data.totalRecords;
          if (typeof totalRecords === 'number' && totalRecords > 0) {
            hasMore = pageNumber * pageSize < totalRecords;
          } else {
            hasMore = members.length === pageSize;
          }

          // Safety: empty pages should terminate.
          if (members.length === 0) {
            hasMore = false;
          }

          pageNumber++;
        }
      } catch (error) {
        // Silently fail for individual groups to avoid breaking the entire page
        // Ignore per-group failures to keep UI responsive
      }
    };

    // Process groups in batches to limit concurrent requests
    for (let i = 0; i < groups.length; i += concurrency) {
      if (signal?.aborted) break;
      const batch = groups.slice(i, i + concurrency);
      // eslint-disable-next-line no-await-in-loop
      await Promise.all(batch.map(fetchGroupMembers));
    }

    // Save to cache
    saveGroupsToCache(memberGroupsMap, organizationId);
  } catch (error) {
    console.warn('Failed to fetch member groups', error);
  }

  return memberGroupsMap;
};

export const useMembersData = (
  props: IMembersTableProps,
  sortBy?: Array<{ id: string; desc?: boolean }>,
) => {
  const history = useHistory();
  const client = useApolloClient();
  const axiosInstance = useAuthenticatedAxios();
  const axiosInstanceRef = useRef(axiosInstance);
  const organizationId = useMemo(() => {
    try {
      return new URLSearchParams(history.location.search).get('organization');
    } catch {
      return null;
    }
  }, [history.location.search]);

  // Normalize cache key so we still fetch/cache groups when no organization query param is present
  const groupsCacheKey = useMemo(
    () => organizationId || 'default',
    [organizationId],
  );

  // Update ref when axiosInstance changes
  useEffect(() => {
    axiosInstanceRef.current = axiosInstance;
  }, [axiosInstance]);

  const [restApiData, setRestApiData] = useState<{
    items: IGetMembersDataItem[];
    totalCount: number;
    loading: boolean;
  }>({ items: [], totalCount: 0, loading: false });

  // State for member groups mapping - initialize from cache if available for immediate display
  const [memberGroupsMap, setMemberGroupsMap] = useState<
    Map<string, IMemberGroup[]>
  >(() => {
    // Try to load from cache on initial mount for immediate display
    const cached = loadGroupsFromCache(groupsCacheKey);
    return normalizeMemberGroupsMap(cached);
  });
  const [memberGroupsVersion, setMemberGroupsVersion] = useState(0);

  const setMemberGroups = useCallback((map: Map<string, IMemberGroup[]>) => {
    // Normalize keys and clone to ensure new reference and trigger memo recalculations
    const nextMap = normalizeMemberGroupsMap(map);
    setMemberGroupsMap(nextMap);
    setMemberGroupsVersion((version) => version + 1);
  }, []);

  const mergeMemberGroups = useCallback(
    (incoming: Map<string, IMemberGroup[]>) => {
      const normalizedIncoming = normalizeMemberGroupsMap(incoming);
      setMemberGroupsMap((prev) => {
        const next = normalizeMemberGroupsMap(prev);
        normalizedIncoming.forEach((value, key) => {
          next.set(key, value);
        });
        return next;
      });
      setMemberGroupsVersion((version) => version + 1);
    },
    [],
  );

  // Track groups loading state to prevent showing incomplete data
  const [groupsLoading, setGroupsLoading] = useState(true);
  // Indicates the slow full groups crawl has completed for the current org.
  // Cached data can be shown immediately, but should not be treated as a full crawl.
  const [groupsReady, setGroupsReady] = useState(false);

  // Track which members have had their groups hydrated for the currently visible page
  // via REST includeGroups. This allows us to treat an empty team list as "resolved"
  // without waiting for the slow full groups crawl (prevents dash -> teams flicker).
  const pageHydratedIdsRef = useRef<Set<string>>(new Set());
  const [pageHydratedVersion, setPageHydratedVersion] = useState(0);

  // Separate loading flag for the fast per-page includeGroups hydration (prevents dash->teams flicker)
  const [pageGroupsLoading, setPageGroupsLoading] = useState(false);

  // When the organization (or cache key) changes, reset groups map (try cache first, then empty)
  useEffect(() => {
    // Try cache first for immediate display, then fetch fresh
    const cached = loadGroupsFromCache(groupsCacheKey);
    setMemberGroups(normalizeMemberGroupsMap(cached));
    setGroupsReady(false);
    // Set loading true - will be cleared when fetch completes
    setGroupsLoading(true);

    // New org/cache key: reset per-page hydration tracking.
    pageHydratedIdsRef.current = new Set();
    setPageHydratedVersion((v) => v + 1);
  }, [groupsCacheKey, setMemberGroups]);

  type MemberLikeForGroupsUi = {
    id: string;
    // Present on REST includeGroups responses and on some GraphQL selections.
    // Kept loose because codegen query types may not match the full schema type.
    groups?: unknown;
  };

  const hasResolvedGroupsForUi = useCallback(
    (item: MemberLikeForGroupsUi) => {
      const normalizedId = normalizeMemberId(item.id);

      // Inline groups (from includeGroups responses) are always authoritative.
      const hasInlineGroups =
        (normalizeGroups((item as any).groups)?.length ?? 0) > 0;
      if (hasInlineGroups) return true;

      // If the groups map already has real data, treat it as resolved even if the full crawl
      // hasn't completed yet.
      const mapGroups =
        memberGroupsMap.get(item.id) ||
        (normalizedId ? memberGroupsMap.get(normalizedId) : undefined) ||
        (normalizedId
          ? memberGroupsMap.get(`OrganizationMember:${normalizedId}`)
          : undefined);
      if ((mapGroups?.length ?? 0) > 0) return true;

      // If we have hydrated this member for the current page, an empty list is still "resolved".
      const hydrated =
        pageHydratedIdsRef.current.has(item.id) ||
        (normalizedId ? pageHydratedIdsRef.current.has(normalizedId) : false) ||
        (normalizedId
          ? pageHydratedIdsRef.current.has(`OrganizationMember:${normalizedId}`)
          : false);
      if (hydrated) return true;

      // Once the full crawl is done, the groups map is considered authoritative (even empty).
      if (groupsReady) {
        return (
          memberGroupsMap.has(item.id) ||
          (normalizedId ? memberGroupsMap.has(normalizedId) : false) ||
          (normalizedId
            ? memberGroupsMap.has(`OrganizationMember:${normalizedId}`)
            : false)
        );
      }

      return false;
    },
    [groupsReady, memberGroupsMap, pageHydratedVersion],
  );

  // State for all members when doing client-side full-dataset sorting (Teams + Individual access)
  const [allMembersForClientSort, setAllMembersForClientSort] = useState<{
    items: IGetMembersDataItem[];
    loading: boolean;
  }>({ items: [], loading: false });

  // Get removed member ID from Apollo cache
  const removedMemberData = client.readQuery({
    query: GET_REMOVED_MEMBER_ID,
  });

  let removedMemberId = removedMemberData?.removedMemberId;
  if (removedMemberId) {
    removedMemberId = removedMemberId.replace('OrganizationMember:', '');
  }

  // Pagination state
  // searchQueryPagingConfig is controlled directly: always starts at page 1 and is
  // updated either by the search-reset effect below or by setPage (called from
  // MembersTable.onChange when the user clicks a page while a search is active).
  const [searchQueryPagingConfig, setSearchQueryPagingConfig] = useState({
    page: 1,
    pageSize: 10,
  });

  // unfilteredPage is the page shown when no search filter is active.
  // Stored as state (not derived from props.match.params.page directly) so it can
  // be explicitly reset to 1 when a search is cleared.
  const [unfilteredPage, setUnfilteredPage] = useState(
    parseInt(props.match.params.page ?? '1', 10),
  );

  const simpleQueryPagingConfig = {
    page: unfilteredPage,
    pageSize: 10,
  };

  // Search state
  const [searchFilter, setSearchFilter] = useState('');
  const debouncedSearchFilter = useDebounce(searchFilter);
  const [searchStringIsValid, setSearchStringIsValid] = useState(true);

  const { page, pageSize } = debouncedSearchFilter
    ? searchQueryPagingConfig
    : simpleQueryPagingConfig;

  // Fetch policy based on navigation state
  const state = history.location.state as { from?: string };
  const fetchPolicy =
    state && state.from === 'member-details'
      ? 'cache-first'
      : 'cache-and-network';

  // Determine if we need REST API for sorting (GraphQL doesn't support sorting)
  // Teams + Individual access require client-side sorting across the full dataset.
  const sortFieldId = sortBy?.[0]?.id;
  const sortFieldDesc = sortBy?.[0]?.desc ?? false;
  const isTeamsSort = sortFieldId === 'teams';
  const isOrgRoleSort = sortFieldId === 'orgRole';
  const isClientSideSort = isTeamsSort || isOrgRoleSort;
  const sortFieldApiName = sortFieldId ? sortFieldMap[sortFieldId] : undefined;
  const useRestApiForSort =
    !!sortFieldApiName &&
    !isClientSideSort &&
    // Use REST for lastLogin when ascending; default lastLogin (desc) uses GraphQL
    (sortFieldId !== 'lastLogin' || sortFieldDesc !== true);

  // GraphQL query (used when not sorting or sorting by lastLogin which is default)
  const { data: membersData, loading: membersLoading } = useGetMembersQuery({
    variables: {
      skip: pageSize * (page - 1),
      take: pageSize,
      query: searchStringIsValid ? debouncedSearchFilter : '',
    },
    fetchPolicy,
    skip: !!useRestApiForSort || isClientSideSort, // Skip GraphQL when using REST API for sorting or client-side full-dataset sort
  });

  // Data processing - use REST API data if sorting, otherwise use GraphQL
  const graphqlItems =
    membersData?.organization?.members?.items ?? EMPTY_ORGANIZATION_MEMBERS;
  const graphqlTotalCount = membersData?.organization?.members?.totalCount || 0;

  const pageGroupsNeeded = useMemo(() => {
    if (useRestApiForSort || isClientSideSort) return false;
    if (!graphqlItems || graphqlItems.length === 0) return false;

    return graphqlItems.some((item) => !hasResolvedGroupsForUi(item));
  }, [
    graphqlItems,
    hasResolvedGroupsForUi,
    useRestApiForSort,
    isClientSideSort,
  ]);

  // For client-side sorting, we use all members so we can sort the full dataset then paginate.
  // For other cases, use normal paginated data sources.
  const rawItems = isClientSideSort
    ? allMembersForClientSort.items
    : useRestApiForSort
    ? restApiData.items
    : graphqlItems;

  // Total count: for client-side sort, use the full dataset size; otherwise use API total
  const totalCount = isClientSideSort
    ? allMembersForClientSort.items.length // Full dataset size for client-side sorting
    : useRestApiForSort
    ? restApiData.totalCount
    : graphqlTotalCount;
  const currentPageHasAllGroups = useMemo(() => {
    // Only relevant for the default GraphQL path (one page of members)
    const itemsToCheck =
      useRestApiForSort || isClientSideSort ? [] : graphqlItems;
    if (!itemsToCheck || itemsToCheck.length === 0) return false;

    return itemsToCheck.every((item) => hasResolvedGroupsForUi(item));
  }, [
    graphqlItems,
    hasResolvedGroupsForUi,
    useRestApiForSort,
    isClientSideSort,
  ]);

  // For UI gating, only consider the fast per-page hydration; the full groups crawl runs in background.
  const effectiveGroupsLoading =
    !useRestApiForSort &&
    !isClientSideSort &&
    (pageGroupsLoading || pageGroupsNeeded) &&
    !currentPageHasAllGroups;
  const loading = isClientSideSort
    ? allMembersForClientSort.loading || effectiveGroupsLoading
    : useRestApiForSort
    ? restApiData.loading || effectiveGroupsLoading
    : membersLoading || effectiveGroupsLoading;

  // Fetch sorted data from REST API when needed
  useEffect(() => {
    const currentAxiosInstance = axiosInstanceRef.current;
    // Early return if conditions aren't met
    if (
      !useRestApiForSort ||
      !currentAxiosInstance ||
      !sortFieldId ||
      !sortFieldApiName
    ) {
      // Only reset if we were previously using REST API to avoid unnecessary updates
      setRestApiData((prev) => {
        if (prev.items.length === 0 && prev.totalCount === 0 && !prev.loading) {
          return prev; // Already in reset state, no need to update
        }
        return { items: [], totalCount: 0, loading: false };
      });
      return;
    }

    const abortController = new AbortController();
    setRestApiData((prev) => ({ ...prev, loading: true }));

    const sortParam = `${sortFieldApiName}:${sortFieldDesc ? '-1' : '1'}`;
    const baseUrl = identity.get_members.url(
      page,
      pageSize,
      searchStringIsValid ? debouncedSearchFilter : '',
    );
    const urlWithSort = `${baseUrl}${
      baseUrl.includes('?') ? '&' : '?'
    }sort=${sortParam}&includeGroups=true`;

    currentAxiosInstance
      .get<IGetMembersResponse>(urlWithSort, {
        signal: abortController.signal,
      })
      .then((response) => {
        // Check if component is still mounted and request wasn't aborted
        if (!abortController.signal.aborted) {
          setRestApiData({
            items: response.data.data || [],
            totalCount: response.data.totalRecords || 0,
            loading: false,
          });
        }
      })
      .catch((error) => {
        // Ignore cancellation errors (component unmounted or dependency changed)
        if (error.name !== 'CanceledError' && error.code !== 'ERR_CANCELED') {
          // Ignore failures; fall back to empty sorted data
          if (!abortController.signal.aborted) {
            setRestApiData({ items: [], totalCount: 0, loading: false });
          }
        }
      });

    return () => {
      abortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    useRestApiForSort,
    page,
    pageSize,
    debouncedSearchFilter,
    searchStringIsValid,
    sortFieldApiName,
    sortFieldId,
    sortFieldDesc,
    // axiosInstance omitted - using ref to avoid infinite loops
    // The ref is updated in a separate useEffect when axiosInstance changes
  ]);

  // Fetch all members when doing client-side sorting across the full dataset.
  useEffect(() => {
    const currentAxiosInstance = axiosInstanceRef.current;

    if (!isClientSideSort || !currentAxiosInstance) {
      setAllMembersForClientSort({ items: [], loading: false });
      return;
    }

    const abortController = new AbortController();
    setAllMembersForClientSort(
      (prev: { items: IGetMembersDataItem[]; loading: boolean }) => ({
        ...prev,
        loading: true,
      }),
    );

    // Fetch all members for client-side sorting
    const fetchAllMembers = async () => {
      try {
        const allMembers: IGetMembersDataItem[] = [];
        let currentPage = 1;
        let hasMore = true;
        const fetchPageSize = 100;
        const maxPages = 1000; // safety net for unexpectedly large/misreported pagination
        let pagesFetched = 0;

        while (hasMore && !abortController.signal.aborted) {
          pagesFetched++;
          if (pagesFetched > maxPages) break;

          const baseUrl = identity.get_members.url(
            currentPage,
            fetchPageSize,
            searchStringIsValid ? debouncedSearchFilter : '',
          );
          const urlWithGroups = `${baseUrl}${
            baseUrl.includes('?') ? '&' : '?'
          }includeGroups=true`;

          const response = await currentAxiosInstance.get<IGetMembersResponse>(
            urlWithGroups,
            { signal: abortController.signal },
          );

          const members = response.data.data || [];
          if (members.length > 0) {
            allMembers.push(...members);
          }

          // Pagination: prefer totalRecords when present, otherwise fall back to "did we receive a full page?"
          const totalRecords = response.data.totalRecords;
          if (typeof totalRecords === 'number' && totalRecords > 0) {
            hasMore = currentPage * fetchPageSize < totalRecords;
          } else {
            hasMore = members.length === fetchPageSize;
          }

          // Safety: empty pages should terminate.
          if (members.length === 0) {
            hasMore = false;
          }

          currentPage++;
        }

        if (!abortController.signal.aborted) {
          setAllMembersForClientSort({ items: allMembers, loading: false });
        }
      } catch (error: unknown) {
        const err = error as { name?: string; code?: string };
        if (err.name !== 'CanceledError' && err.code !== 'ERR_CANCELED') {
          // Ignore failures; fall back to empty client-side sort data
          if (!abortController.signal.aborted) {
            setAllMembersForClientSort({ items: [], loading: false });
          }
        }
      }
    };

    fetchAllMembers();

    return () => {
      abortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClientSideSort, debouncedSearchFilter, searchStringIsValid]);

  // When using the default GraphQL path (no REST sorting), eagerly hydrate groups for the
  // currently visible page via REST includeGroups to render Teams without waiting for the
  // full groups crawl.
  useEffect(() => {
    if (useRestApiForSort || isClientSideSort) return;

    const currentAxiosInstance = axiosInstanceRef.current;
    if (!currentAxiosInstance) return;
    if (!graphqlItems || graphqlItems.length === 0) return;

    // If every visible member already has groups, skip the hydration call
    const missingIds = graphqlItems
      .map((item) => normalizeMemberId(item.id))
      .filter((id, index) => {
        if (!id) return false;
        const item = graphqlItems[index];
        return item ? !hasResolvedGroupsForUi(item) : false;
      });
    if (missingIds.length === 0) return;

    const abortController = new AbortController();
    setPageGroupsLoading(true);
    const baseUrl = identity.get_members.url(
      page,
      pageSize,
      searchStringIsValid ? debouncedSearchFilter : '',
    );
    const urlWithGroups = `${baseUrl}${
      baseUrl.includes('?') ? '&' : '?'
    }includeGroups=true`;

    currentAxiosInstance
      .get<IGetMembersResponse>(urlWithGroups, {
        signal: abortController.signal,
      })
      .then((response) => {
        if (abortController.signal.aborted) return;

        const items = response.data.data || [];
        if (items.length === 0) return;

        // Merge using a functional update to avoid races with the background crawl.
        setMemberGroupsMap((prev) => {
          const nextMap = normalizeMemberGroupsMap(prev);

          items.forEach((item) => {
            const groups = normalizeGroups((item as any).groups) ?? [];
            const normalizedId = normalizeMemberId(item.id);

            // Don't clobber an existing non-empty value with an empty one.
            const existingGroups =
              nextMap.get(item.id) ||
              (normalizedId ? nextMap.get(normalizedId) : undefined) ||
              (normalizedId
                ? nextMap.get(`OrganizationMember:${normalizedId}`)
                : undefined);
            const nextGroups =
              groups.length === 0 && (existingGroups?.length ?? 0) > 0
                ? existingGroups!
                : groups;

            nextMap.set(item.id, nextGroups);
            if (normalizedId && normalizedId !== item.id) {
              nextMap.set(normalizedId, nextGroups);
            }

            // Mark as hydrated so empty teams can be considered resolved immediately.
            pageHydratedIdsRef.current.add(item.id);
            if (normalizedId) {
              pageHydratedIdsRef.current.add(normalizedId);
              pageHydratedIdsRef.current.add(
                `OrganizationMember:${normalizedId}`,
              );
            }
          });

          return nextMap;
        });
        setMemberGroupsVersion((version) => version + 1);
        setPageHydratedVersion((v) => v + 1);
      })
      .catch(() => {
        if (abortController.signal.aborted) return;
        // If hydration fails, don't block rendering indefinitely. Mark the current
        // page members as "hydrated" so the UI can proceed with whatever data it has.
        graphqlItems.forEach((item) => {
          const normalizedId = normalizeMemberId(item.id);
          pageHydratedIdsRef.current.add(item.id);
          if (normalizedId) {
            pageHydratedIdsRef.current.add(normalizedId);
            pageHydratedIdsRef.current.add(
              `OrganizationMember:${normalizedId}`,
            );
          }
        });
        setPageHydratedVersion((v) => v + 1);
      })
      .finally(() => {
        if (!abortController.signal.aborted) {
          setPageGroupsLoading(false);
        }
      });

    return () => {
      abortController.abort();
      setPageGroupsLoading(false);
    };
  }, [
    useRestApiForSort,
    isClientSideSort,
    graphqlItems,
    memberGroupsMap,
    hasResolvedGroupsForUi,
    page,
    pageSize,
    searchStringIsValid,
    debouncedSearchFilter,
  ]);

  // Tracks whether the previous render had an active search filter, so the
  // combined effect below can detect the search-cleared transition.
  const prevDebouncedSearchFilterRef = useRef(debouncedSearchFilter);

  // Ref that exposes the latest debouncedSearchFilter value to setPage and
  // the URL-sync effect without making them reactive to search-term changes.
  const debouncedSearchFilterRef = useRef(debouncedSearchFilter);

  // Single effect for all debouncedSearchFilter-dependent work:
  //  • Keeps debouncedSearchFilterRef in sync for use in setPage / URL-sync.
  //  • Any change while search is active → reset search-mode page to 1.
  //  • Clearing the search (non-empty → empty) → reset unfiltered page to 1
  //    so the user always lands on unfiltered page 1, not the stale URL page.
  useEffect(() => {
    const wasSearchActive = prevDebouncedSearchFilterRef.current !== '';
    prevDebouncedSearchFilterRef.current = debouncedSearchFilter;
    debouncedSearchFilterRef.current = debouncedSearchFilter;

    if (debouncedSearchFilter) {
      setSearchQueryPagingConfig((prev) => ({ ...prev, page: 1 }));
    } else if (wasSearchActive) {
      setUnfilteredPage(1);
    }
  }, [debouncedSearchFilter]);

  // Direct page setter used by MembersTable.onChange for any user-initiated page
  // navigation. Routes to the correct internal state based on whether search is
  // active at the time of the call, using a ref so the callback is always stable
  // and never reads a stale closure value.

  // Keep unfilteredPage in sync with URL-driven navigation (user clicking a page
  // while no search is active). Reads search state via ref so debouncedSearchFilter
  // is not a reactive dependency — this effect must only fire on page param changes.
  useEffect(() => {
    if (!debouncedSearchFilterRef.current) {
      setUnfilteredPage(parseInt(props.match.params.page ?? '1', 10));
    }
  }, [props.match.params.page]);

  const setPage = useCallback((newPage: number) => {
    if (debouncedSearchFilterRef.current) {
      setSearchQueryPagingConfig((prev) => ({ ...prev, page: newPage }));
    } else {
      setUnfilteredPage(newPage);
    }
  }, []);

  // Ref to track current abort controller
  const abortControllerRef = useRef<AbortController | null>(null);
  // Ref to track organizationId for event handler
  const organizationIdRef = useRef<string | null>(groupsCacheKey);

  // Update ref when organizationId changes
  useEffect(() => {
    organizationIdRef.current = groupsCacheKey;
  }, [groupsCacheKey, setMemberGroups]);

  // Fetch groups for members (used to enrich GraphQL members with Teams data).
  // Always fetch fresh data on mount and when cache is cleared.
  useEffect(() => {
    const currentAxiosInstance = axiosInstanceRef.current;
    if (!currentAxiosInstance) return;

    const hasExistingGroups = memberGroupsMap.size > 0;

    const fetchGroups = (bypassCache = true) => {
      // Abort previous request if any
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      // Keep existing data visible during refresh; only gate when we have nothing yet
      setGroupsLoading(true);
      if (!hasExistingGroups) {
        setGroupsReady(false);
      }

      // Fetch fresh data (bypass cache to ensure accuracy)
      fetchMemberGroups(
        currentAxiosInstance,
        groupsCacheKey,
        abortController.signal,
        bypassCache,
      )
        .then((map) => {
          if (!abortController.signal.aborted) {
            // Optional debug: fetched member groups map size
            // Merge instead of replace to avoid dropping per-page "includeGroups" hydration
            // entries (especially for members with no teams).
            mergeMemberGroups(map);
            setGroupsLoading(false);
            setGroupsReady(true);
          }
        })
        .catch(() => {
          if (!abortController.signal.aborted) {
            // Ignore fetch errors; keep existing map if present
            // On error, don't clear existing map - keep what we have (from cache or previous fetch)
            setGroupsLoading(false);
            setGroupsReady(true);
          }
        });

      return abortController;
    };

    // Always fetch fresh data on mount (bypass cache)
    fetchGroups(true);

    // Listen for cache clearing events
    const handleCacheCleared = (event: Event) => {
      const customEvent = event as CustomEvent<{ organizationId: string }>;
      const eventOrgId = customEvent.detail?.organizationId;
      const currentOrgId = organizationIdRef.current;
      const eventKey = eventOrgId || 'default';
      const currentKey = currentOrgId || 'default';

      // Refetch when the cache clear event matches the current org key (including default)
      if (eventKey === currentKey) {
        fetchGroups(true);
      }
    };

    window.addEventListener('memberGroupsCacheCleared', handleCacheCleared);

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      window.removeEventListener(
        'memberGroupsCacheCleared',
        handleCacheCleared,
      );
    };
  }, [groupsCacheKey, mergeMemberGroups]);

  // Transform REST API data to match GraphQL format if needed
  const transformedItems = useMemo(() => {
    let items: OrganizationMember[];

    // Don't process client-side sorting until we have the full dataset
    if (
      isClientSideSort &&
      (allMembersForClientSort.loading || rawItems.length === 0)
    ) {
      return [];
    }

    if (isClientSideSort && rawItems.length > 0) {
      // Transform REST API format for client-side sorting (all members)
      // rawItems is IGetMembersDataItem[] when isClientSideSort is true
      items = (rawItems as IGetMembersDataItem[]).map(
        (item: IGetMembersDataItem) => {
          // Use groups from memberGroupsMap first (most reliable), then API response, then null
          // memberGroupsMap is built from groups API which is more accurate
          const normalizedId = normalizeMemberId(item.id);
          const mapGroups =
            memberGroupsMap.get(item.id) ||
            memberGroupsMap.get(normalizedId) ||
            undefined;
          const groupsFromMap = normalizeGroups(mapGroups);
          const groupsFromApi = normalizeGroups((item as any).groups);
          const groups = groupsFromMap || groupsFromApi || null;
          return {
            id: item.id,
            email: item.email,
            emailVerified: item.emailVerified,
            created: item.created,
            updated: item.updated,
            signUpCompleted: item.signUpCompleted,
            isExternal: false,
            givenName: item.givenName,
            familyName: item.familyName,
            nickName: item.nickName || '',
            phoneNumber: item.phoneNumber || '',
            company: item.company || '',
            jobRole: item.jobRole || '',
            preferredLanguage: item.preferredLanguage || '',
            picture: item.picture || '',
            lastLogin: item.lastLogin,
            organizationRoles: {
              nodes:
                item.roles
                  ?.filter((r: IMemberRole) => r.scope === 'Organization')
                  .map((r: IMemberRole) => ({
                    name: r.role,
                    description: '',
                    organizationId: r.organizationId,
                  })) || [],
            },
            appearance: {
              memberActions: {
                __typename: 'MemberActionsConnection' as const,
                totalCount: 0,
                nodes: [],
                pageInfo: {
                  __typename: 'PageInfo' as const,
                  hasNextPage: false,
                  hasPreviousPage: false,
                  startCursor: null,
                  endCursor: null,
                },
              },
            },
            groups: groups,
          };
        },
      ) as unknown as OrganizationMember[];
    } else if (useRestApiForSort && restApiData.items.length > 0) {
      // Transform REST API format to GraphQL-like format for transformDataForTable
      items = restApiData.items.map((item) => {
        // Use groups from API response if available, otherwise use the memberGroupsMap
        const normalizedId = normalizeMemberId(item.id);
        const mapGroups =
          memberGroupsMap.get(item.id) ||
          memberGroupsMap.get(normalizedId) ||
          undefined;
        const groups =
          normalizeGroups((item as any).groups) ||
          normalizeGroups(mapGroups) ||
          null;
        return {
          id: item.id,
          email: item.email,
          emailVerified: item.emailVerified,
          created: item.created,
          updated: item.updated,
          signUpCompleted: item.signUpCompleted,
          isExternal: false,
          givenName: item.givenName,
          familyName: item.familyName,
          nickName: item.nickName || '',
          phoneNumber: item.phoneNumber || '',
          company: item.company || '',
          jobRole: item.jobRole || '',
          preferredLanguage: item.preferredLanguage || '',
          picture: item.picture || '',
          lastLogin: item.lastLogin,
          organizationRoles: {
            nodes:
              item.roles
                ?.filter((r: IMemberRole) => r.scope === 'Organization')
                .map((r: IMemberRole) => ({
                  name: r.role,
                  description: '',
                  organizationId: r.organizationId,
                })) || [],
          },
          appearance: {
            memberActions: {
              __typename: 'MemberActionsConnection' as const,
              totalCount: 0,
              nodes: [],
              pageInfo: {
                __typename: 'PageInfo' as const,
                hasNextPage: false,
                hasPreviousPage: false,
                startCursor: null,
                endCursor: null,
              },
            },
          },
          groups: groups,
        };
      }) as unknown as OrganizationMember[];
    } else {
      // For GraphQL items, enrich with groups from memberGroupsMap
      items = graphqlItems.map((item) => {
        const normalizedId = normalizeMemberId(item.id);
        const mapGroups =
          memberGroupsMap.get(item.id) ||
          memberGroupsMap.get(normalizedId) ||
          undefined;
        const groupsFromMap = normalizeGroups(mapGroups);
        const groupsFromGraphql = normalizeGroups((item as any).groups);

        const groups =
          (groupsFromMap && groupsFromMap.length > 0 ? groupsFromMap : null) ||
          (groupsFromGraphql && groupsFromGraphql.length > 0
            ? groupsFromGraphql
            : null);
        return {
          ...item,
          // Prefer the enriched map only when it has data; otherwise keep GraphQL groups.
          groups,
        };
      }) as OrganizationMember[];
    }

    // Client-side sorting (sort entire dataset, then paginate)
    if (items.length > 0) {
      if (isTeamsSort) {
        items = sortMembersForTeamsColumn(items, sortFieldDesc, page, pageSize);
      } else if (isOrgRoleSort) {
        items = sortMembersForOrgRoleColumn(
          items,
          sortFieldDesc,
          page,
          pageSize,
        );
      }
    }

    return items;
  }, [
    isClientSideSort,
    isTeamsSort,
    isOrgRoleSort,
    rawItems,
    useRestApiForSort,
    restApiData.items,
    graphqlItems,
    memberGroupsMap,
    memberGroupsVersion,
    sortFieldDesc,
    page,
    pageSize,
    allMembersForClientSort.loading, // Include loading state to ensure we wait
  ]);

  const cachedFilteredItems = transformedItems.filter(
    (member) => member.id !== removedMemberId,
  );

  // Hide rows while groups are still loading to avoid flashing placeholder dashes
  // Hold rows until groups are ready to avoid flashing placeholder dashes
  const shouldHoldRows =
    !currentPageHasAllGroups &&
    (pageGroupsLoading || pageGroupsNeeded) &&
    !useRestApiForSort &&
    !isClientSideSort;

  const memberToShow = useMemo(
    () => (shouldHoldRows ? [] : transformDataForTable(cachedFilteredItems)),
    [cachedFilteredItems, shouldHoldRows],
  );

  // If GraphQL already provides groups and we have no map yet, hydrate the map so Teams render without waiting for REST
  useEffect(() => {
    if (memberGroupsMap.size > 0) return;
    if (!graphqlItems || graphqlItems.length === 0) return;

    const provisionalMap = new Map<string, IMemberGroup[]>();
    graphqlItems.forEach((item) => {
      const norm = normalizeGroups((item as any).groups);
      if (norm && norm.length > 0) {
        const normalizedId = normalizeMemberId(item.id);
        provisionalMap.set(item.id, norm);
        if (normalizedId && normalizedId !== item.id) {
          provisionalMap.set(normalizedId, norm);
        }
      }
    });

    if (provisionalMap.size > 0) {
      setMemberGroups(provisionalMap);
    }
  }, [graphqlItems, memberGroupsMap.size, setMemberGroups]);

  const pagination = totalCount < pageSize ? 'hidden' : 'complex';

  return {
    // Data
    memberToShow,
    totalCount,
    membersLoading: loading,
    groupsReady,
    groupsLoading,
    pageGroupsLoading,
    pageGroupsNeeded,

    // Search state
    searchFilter,
    setSearchFilter,
    searchStringIsValid,
    setSearchStringIsValid,

    // Pagination state
    page,
    pageSize,
    pagination,
    setPage,

    // History
    history,
  };
};
